/**
 * bundle-addons.js
 * Custom element that turns Focal's complementary-products slot into an
 * add-on bundle UI: checkboxes, discount-aware prices, per-axis variant
 * dropdowns.
 *
 * Source-of-truth for which products show up: Search & Discovery app
 * (Shopify native Complementary Products).
 *
 * Source-of-truth for "is this an add-on?": product metafield
 *   custom.bundle_discount_percent (integer, 0-50)
 *
 * Discount itself is applied server-side by a Shopify Function reading
 * the same metafield — this file only renders the visual state and
 * extends the host product-form with the selected add-ons.
 *
 * No external deps. Inherits Focal's cart-add pipeline via the host
 * product-form, so cart-drawer refresh + discount badge display
 * (line_level_discount_allocations in mini-cart.liquid) come for free.
 */

(() => {
  if (window.customElements.get('bundle-addons-form')) return;

  /**
   * Look up the host product-form on the page. Focal renders the main
   * buy-form with id starting with `product-form-main-` (see
   * sections/main-product.liquid: capture product_form_id). Recommended
   * products on the same PDP also render product-forms (quick-buy on
   * product-item snippet) — we want the MAIN one, never a quick-buy.
   */
  const getHostProductForm = () => {
    return document.querySelector('form[is="product-form"][id^="product-form-main-"]')
      || document.querySelector('main form[is="product-form"]')
      || document.querySelector('form[is="product-form"]');
  };

  class BundleAddonsForm extends HTMLElement {
    connectedCallback() {
      this.addonItems = Array.from(this.querySelectorAll('[data-bundle-addon]'));
      if (!this.addonItems.length) return;

      this.hostForm = getHostProductForm();
      this._bindCheckboxes();
      this._bindVariantSelects();
      this._bindHostFormSubmit();
      this._bindMoreDrawer();
      this._emitShownEvents();
    }

    _bindCheckboxes() {
      this.addonItems.forEach((item) => {
        const checkbox = item.querySelector('[data-bundle-addon-checkbox]');
        if (!checkbox) return;

        // Whole card is the click-target on mobile + desktop.
        item.addEventListener('click', (event) => {
          // Don't double-fire when the user actually clicked the checkbox
          // or a dropdown.
          if (event.target.closest('[data-bundle-addon-option]')) return;
          if (event.target === checkbox) return;
          // Ignore clicks on links inside the card (e.g. product title link
          // to PDP, if we ever add one).
          if (event.target.closest('a')) return;
          event.preventDefault();
          checkbox.checked = !checkbox.checked;
          this._onToggle(item, checkbox.checked);
        });

        checkbox.addEventListener('change', () => {
          this._onToggle(item, checkbox.checked);
        });
      });
    }

    _onToggle(item, checked) {
      item.classList.toggle('bundle-addon--checked', checked);
      this._renderPicked();

      const productId = item.dataset.bundleAddonProductId;
      const variantId = this._getSelectedVariantId(item);
      const handle = item.dataset.bundleAddonHandle;

      document.documentElement.dispatchEvent(new CustomEvent(
        checked ? 'bundle:addon:added' : 'bundle:addon:removed',
        { bubbles: true, detail: { productId, variantId, handle } }
      ));
    }

    // One <select> per varying option axis (see bundle-addon-card.liquid).
    // On any change, resolve the full combo of selected values against the
    // embedded variant list to update price/image/selected-variant-id.
    //
    // Greying is HIERARCHICAL, like Shopify's own option-value availability:
    // a value is only greyed if no available variant exists for it given the
    // selects ABOVE it. Lower selects never lock upper ones. Checking against
    // ALL other selects (the old behaviour) dead-locks products whose colour
    // set differs per fabric: with 'Nook' on Cord/Ecru every other Stoffart
    // was greyed, because "Teddy-Bouclé + Ecru" does not exist — the shopper
    // could never leave Cord. Picking an upper value whose current combo does
    // not exist (or is sold out) jumps the lower selects to the first
    // available variant under it.
    _bindVariantSelects() {
      this.addonItems.forEach((item) => {
        const selects = Array.from(item.querySelectorAll('[data-bundle-addon-option]'));
        if (!selects.length) return;

        const variantsScript = item.querySelector('[data-bundle-addon-variants]');
        if (!variantsScript) return;

        let variants;
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (err) {
          return;
        }

        // A non-varying axis (e.g. "Ausführung" with a single value) has no
        // <select> at all, but still occupies a slot in every variant's
        // `options` array. Match by each select's real Shopify option
        // POSITION, not by its position among the rendered selects, or a
        // skipped leading axis would silently shift every comparison.
        const positions = selects.map((select) => parseInt(select.dataset.bundleAddonOptionPosition, 10) - 1);
        const matches = (v, values) => positions.every((pos, i) => v.options[pos] === values[i]);
        // Like `matches`, but only for the first `count` selects.
        const matchesPrefix = (v, values, count) => positions.slice(0, count).every((pos, i) => v.options[pos] === values[i]);

        const applySelection = (changedIdx = -1) => {
          let selected = selects.map((select) => select.value);

          if (changedIdx >= 0) {
            const exact = variants.find((v) => v.available && matches(v, selected));
            if (!exact) {
              const fallback = variants.find((v) => v.available && matchesPrefix(v, selected, changedIdx + 1));
              if (fallback) {
                selects.forEach((select, i) => {
                  if (i > changedIdx) select.value = fallback.options[positions[i]];
                });
                selected = selects.map((select) => select.value);
              }
            }
          }

          selects.forEach((select, idx) => {
            Array.from(select.options).forEach((opt) => {
              const trial = selected.slice(0, idx);
              trial[idx] = opt.value;
              opt.disabled = !variants.some((v) => v.available && matchesPrefix(v, trial, idx + 1));
            });
          });

          const match = variants.find((v) => matches(v, selected));
          if (!match) return;

          item.dataset.selectedVariantId = String(match.id);

          // Keep the info chip pointing at the variant the card currently shows,
          // so the PDP it opens matches the image and price right next to it.
          const infoLink = item.querySelector('[data-bundle-addon-info]');
          if (infoLink && infoLink.dataset.bundleAddonUrl) {
            infoLink.href = `${infoLink.dataset.bundleAddonUrl}?variant=${match.id}`;
          }

          // Only on a real selection change: on page load bundle-addon-card.liquid
          // already rendered the preselected variant's image (with srcset).
          if (match.image && changedIdx >= 0) {
            const img = item.querySelector('[data-bundle-addon-image]');
            if (img) {
              img.src = match.image;
              if (img.srcset) img.srcset = '';
            }
          }

          const current = item.querySelector('[data-bundle-addon-price-current]');
          const compare = item.querySelector('[data-bundle-addon-price-compare]');
          if (compare) {
            if (current && match.priceDiscounted) current.textContent = match.priceDiscounted;
            compare.textContent = match.price;
          } else if (current) {
            current.textContent = match.price;
          }

          const checkbox = item.querySelector('[data-bundle-addon-checkbox]');
          if (checkbox) checkbox.disabled = !match.available;
        };

        selects.forEach((select, idx) => select.addEventListener('change', () => applySelection(idx)));
        applySelection();
      });
    }

    _getSelectedVariantId(item) {
      return item.dataset.selectedVariantId || item.dataset.bundleAddonDefaultVariantId;
    }

    _bindHostFormSubmit() {
      if (!this.hostForm) return;
      // Guard against double-binding if the custom element re-connects (e.g. a
      // theme-editor section re-render): the host form persists, so a second
      // bind would attach duplicate listeners and double-add the add-ons. (N-2)
      if (this.hostForm.dataset.bundleAddonsHooked) return;
      this.hostForm.dataset.bundleAddonsHooked = '1';

      // Cart-type branch (M-3). The drawer flow below hangs off Focal's
      // `variant:added` → `cart:refresh` sequence. In a PAGE-cart config Focal
      // redirects straight to /cart after the main add and never dispatches
      // `variant:added`, so that flow would silently never fire. For page-cart
      // we add the checked add-ons on submit with a keepalive fetch that
      // survives the navigation; both items land in the cart shown on /cart and
      // the (cart-state) discount applies there. mokebo runs the drawer, so this
      // path is dormant here — it just stops the feature dying silently if the
      // cart type is ever flipped.
      const themeSettings = (window.themeVariables && window.themeVariables.settings) || {};
      if (themeSettings.cartType === 'page') {
        this.hostForm.addEventListener('submit', () => {
          // Mirror Focal's own guard: on a failed validity check Focal does NOT
          // add the main product, so we must not add an orphan add-on either.
          if (typeof this.hostForm.checkValidity === 'function' && !this.hostForm.checkValidity()) return;
          const ids = this._checkedAddonVariantIds();
          if (ids.length) {
            this._addAddons(ids, { keepalive: true, skipRefresh: true, hostVariantId: this._getHostVariantId() });
          }
        });
        return;
      }

      // Strategy: do NOT mutate the host form. Let Focal add the main product
      // exactly as it normally would — with all its line-item properties
      // (e.g. properties[Versand] shipping time) and quantity intact — then
      // add the checked add-ons as a SEPARATE /cart/add.js request. This
      // guarantees the core add-to-cart can never break because of a bundle
      // bug; worst case the add-ons are silently skipped.
      //
      // Why not inject items[] into the host form? Focal builds its payload
      // with `new FormData(this)`, which pulls in form-ASSOCIATED inputs
      // (quantity, variant options live OUTSIDE the <form> via form="…").
      // A single items[] payload would also drop the host's top-level
      // properties[] (Shopify ignores them when items[] is present), losing
      // the Versand shipping-time property. A separate add sidesteps all of
      // that.
      //
      // Focal's product-form dispatches `variant:added` on the form after a
      // successful main add, then its own `cart:refresh` on documentElement.
      // We snapshot the checked add-ons on `variant:added`, then fire our add
      // AFTER Focal's `cart:refresh` so OUR refresh (with the add-ons) is the
      // last word on the mini-cart contents — no race over the drawer state.
      // On a successful main add (variant:added), wait for the NEXT cart:refresh
      // (Focal's, fired right after the main add) and fire our add-on add
      // exactly once via a ONE-SHOT listener. We deliberately do NOT keep a
      // persistent pending flag: a one-shot listener + timeout means an
      // unrelated later cart:refresh (e.g. a cart-drawer quantity change) can
      // never trigger a stale add-on add. (N-1)
      this.hostForm.addEventListener('variant:added', () => {
        if (this._addonAddInFlight) return;
        const ids = this._checkedAddonVariantIds();
        if (!ids.length) return;
        // Capture the host's variant NOW (at the moment its own add just
        // succeeded), not later when _addAddons actually fires — the
        // shopper could change the variant picker in between.
        const hostVariantId = this._getHostVariantId();

        let consumed = false;
        const onRefresh = () => {
          if (consumed) return;
          consumed = true;
          clearTimeout(timer);
          document.documentElement.removeEventListener('cart:refresh', onRefresh);
          this._addonAddInFlight = true;
          this._addAddons(ids, { hostVariantId }).finally(() => { this._addonAddInFlight = false; });
        };
        // If Focal's cart:refresh never arrives (e.g. its cart.js fetch failed),
        // drop the listener so it can never fire on a later unrelated refresh.
        const timer = setTimeout(() => {
          consumed = true;
          document.documentElement.removeEventListener('cart:refresh', onRefresh);
        }, 5000);
        document.documentElement.addEventListener('cart:refresh', onRefresh);
      });
    }

    _checkedAddonVariantIds() {
      return this.addonItems
        .filter((item) => {
          const cb = item.querySelector('[data-bundle-addon-checkbox]');
          return cb && cb.checked;
        })
        .map((item) => this._getSelectedVariantId(item))
        .filter(Boolean);
    }

    // The host's currently-selected variant id, read straight off its own
    // form (the same field Focal's own product-form submit reads). Used to
    // tag each add-on with properties[_bundle_for] so mini-cart.liquid's
    // line-item sort can always place the add-on directly after its host,
    // regardless of the order Shopify's cart API actually returns (see the
    // sort comment in mini-cart.liquid for why that order isn't guaranteed).
    _getHostVariantId() {
      if (!this.hostForm) return null;
      const id = new FormData(this.hostForm).get('id');
      return id ? String(id) : null;
    }

    async _addAddons(variantIds, opts) {
      opts = opts || {};
      const routes = (window.themeVariables && window.themeVariables.routes) || {};
      const addUrl = `${routes.cartAddUrl || '/cart/add'}.js`;
      const cartUrl = `${routes.cartUrl || '/cart'}.js`;

      const body = new FormData();
      variantIds.forEach((id, i) => {
        body.append(`items[${i}][id]`, id);
        body.append(`items[${i}][quantity]`, '1');
        if (opts.hostVariantId) {
          body.append(`items[${i}][properties][_bundle_for]`, opts.hostVariantId);
        }
      });
      // Ask for the mini-cart section back so we can re-render the drawer in
      // the same round-trip, mirroring Focal's own add flow.
      body.append('sections', 'mini-cart');

      try {
        const fetchOpts = {
          method: 'POST',
          body,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        };
        // page-cart: the page is about to navigate to /cart, so let the request
        // outlive the unload (M-3).
        if (opts.keepalive) fetchOpts.keepalive = true;

        const res = await fetch(addUrl, fetchOpts);
        const json = await res.json();
        if (!res.ok) {
          // The main product is already in the cart; skip the add-ons but tell
          // the customer so a checked add-on can't vanish silently (M-1).
          // eslint-disable-next-line no-console
          console.error('[bundle-addons] add-on add rejected', json && json.description);
          if (!opts.skipRefresh) this._notifyAddonFailure();
          return;
        }

        // page-cart path is navigating away — no drawer to refresh, nothing to
        // uncheck (the page reloads).
        if (opts.skipRefresh) return;

        // Refresh cart + mini-cart so the add-ons (and the discount) show up.
        // Same event shape Focal uses. replacementDelay MUST outlast Focal's own
        // deferred drawer render: Focal sets nextReplacementDelay=600ms on
        // `variant:added`, so its (main-only) render lands ~600ms after the
        // click. Without a larger delay here our full-cart render fires first
        // and Focal's later render overwrites it, dropping the add-on from the
        // drawer. 700ms guarantees ours is the last word. (M-2)
        const cart = await (await fetch(cartUrl)).json();
        cart.sections = json.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart, openMiniCart: false, replacementDelay: 700 }
        }));

        // Uncheck the added add-ons so a subsequent main add won't re-add them.
        this.addonItems.forEach((item) => {
          const cb = item.querySelector('[data-bundle-addon-checkbox]');
          if (cb && cb.checked) {
            cb.checked = false;
            item.classList.remove('bundle-addon--checked');
          }
        });
        this._renderPicked();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[bundle-addons] add-on add failed', err);
        if (!opts.skipRefresh) this._notifyAddonFailure();
      }
    }

    // Surface a non-blocking error via Focal's cart-notification (M-1). The
    // localized message is passed in from Liquid on the host element; if it is
    // absent we stay silent rather than show an English fallback.
    _notifyAddonFailure() {
      const message = this.dataset.addonFailedMessage;
      if (!message) return;
      document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
        bubbles: true,
        cancelable: true,
        detail: { status: 'error', error: message }
      }));
    }

    _updateAddToCartHint() {
      // Optional: append "(+1)" count to the host submit button.
      // Off by default — design decision, leave commented to allow a
      // pure non-invasive Phase-1 launch. Enable per section-setting later.
    }

    // "+ N weitere" tile (product-content.liquid): add-ons beyond the visible
    // cards live in a Focal <drawer-content> INSIDE this element, so they are
    // already part of this.addonItems — checkbox, variant selects and the host
    // add-to-cart flow need nothing extra. This only keeps the tile's
    // "Ausgewählt" list and the drawer's done button in sync with them.
    _bindMoreDrawer() {
      this.moreEl = this.querySelector('[data-bundle-addons-more]');
      this.drawerEl = this.querySelector('.bundle-addons__drawer');
      if (!this.moreEl || !this.drawerEl) return;

      this.pickedEl = this.moreEl.querySelector('[data-bundle-addons-picked]');
      this.pickedListEl = this.moreEl.querySelector('[data-bundle-addons-picked-list]');
      this.doneButton = this.drawerEl.querySelector('[data-bundle-addons-done]');
      this.drawerItems = this.addonItems.filter((item) => this.drawerEl.contains(item));

      // A variant change on a picked add-on changes its name/price in the list.
      this.drawerEl.addEventListener('change', (event) => {
        if (event.target.closest('[data-bundle-addon-option]')) this._renderPicked();
      });

      // Drawer add-ons only count as "shown" once the drawer is actually opened.
      this.drawerEl.addEventListener('openable-element:open', () => {
        if (this._drawerShownEmitted) return;
        this._drawerShownEmitted = true;
        this._emitShownEvents(this.drawerItems);
      });
    }

    _renderPicked() {
      if (!this.moreEl) return;

      const picked = this.drawerItems.filter((item) => {
        const cb = item.querySelector('[data-bundle-addon-checkbox]');
        return cb && cb.checked;
      });

      this.moreEl.classList.toggle('bundle-addons__more--has', picked.length > 0);
      this.pickedEl.hidden = picked.length === 0;
      this.pickedListEl.replaceChildren(...picked.map((item) => this._pickedRow(item)));

      if (this.doneButton) {
        const done = this.moreEl.dataset.doneLabel || '';
        const selected = (this.moreEl.dataset.selectedLabel || '').replace('[COUNT]', String(picked.length));
        this.doneButton.textContent = picked.length ? `${done} · ${selected}` : done;
      }
    }

    // Built with textContent, never innerHTML: titles and variant names are
    // merchant data.
    _pickedRow(item) {
      const row = document.createElement('li');
      row.className = 'bundle-addons__picked-item';

      const source = item.querySelector('[data-bundle-addon-image]');
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      if (source) img.src = source.currentSrc || source.src;

      const title = item.querySelector('.bundle-addon__title');
      const name = document.createElement('span');
      name.className = 'bundle-addons__picked-name';
      name.textContent = title ? title.textContent.trim() : '';

      const values = Array.from(item.querySelectorAll('[data-bundle-addon-option]')).map((select) => select.value);
      if (values.length) {
        const variant = document.createElement('span');
        variant.className = 'bundle-addons__picked-variant';
        variant.textContent = values.join(' / ');
        name.appendChild(variant);
      }

      const priceSource = item.querySelector('[data-bundle-addon-price-current]');
      const price = document.createElement('span');
      price.className = 'bundle-addons__picked-price';
      price.textContent = priceSource ? priceSource.textContent.trim() : '';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'bundle-addons__picked-remove';
      remove.textContent = '×';
      remove.setAttribute('aria-label', (this.moreEl.dataset.removeLabel || '').replace('[PRODUCT]', name.firstChild ? name.firstChild.textContent : ''));
      remove.addEventListener('click', () => {
        const cb = item.querySelector('[data-bundle-addon-checkbox]');
        if (!cb) return;
        cb.checked = false;
        this._onToggle(item, false);
      });

      row.append(img, name, price, remove);
      return row;
    }

    // Drawer add-ons are left out at page load and reported once the drawer
    // opens (see _bindMoreDrawer).
    _emitShownEvents(items) {
      const shown = items || this.addonItems.filter((item) => !this.drawerItems || !this.drawerItems.includes(item));
      shown.forEach((item) => {
        document.documentElement.dispatchEvent(new CustomEvent('bundle:addon:shown', {
          bubbles: true,
          detail: {
            productId: item.dataset.bundleAddonProductId,
            handle: item.dataset.bundleAddonHandle,
            discountPercent: parseInt(item.dataset.bundleAddonDiscountPercent || '0', 10)
          }
        }));
      });
    }
  }

  window.customElements.define('bundle-addons-form', BundleAddonsForm);
})();
